import type { SignalSurfaces, SignalValues } from './conciergeState'
import {
  CITY_LOOKUP,
  INTEREST_LOOKUP,
  INTEREST_NAMES,
  MAX_NGRAM,
  OCCASION_LOOKUP,
  RELATIONSHIP_LOOKUP,
  matchBudget,
  normalizeKey,
} from './signalDictionary'

export type ParsedSignals = {
  signals: Partial<SignalValues>
  // The words the buyer actually used for each signal above, when they differ from
  // the canonical it resolved to — "husband" for spouse. A sibling map rather than
  // pairs inside `signals` so every existing `parsed.signals.x` read still works.
  surfaces: SignalSurfaces
  // Tokens no dictionary claimed. Kept, not dropped: they're the raw material
  // for a future meaning-match path and for spotting words worth adding.
  unmatched: string[]
}

export type SignalParser = (raw: string) => Promise<ParsedSignals>

// Programmatic keyword matcher — no AI. Tokenizes the buyer's text, walks it
// left-to-right taking the longest dictionary phrase at each position, and reads
// a budget straight out of the string. A production parser (a fuller word list,
// then an LLM) swaps in behind the same SignalParser interface without touching
// the state model or the input UI.
export const parseSignals: SignalParser = async raw => matchSignals(raw)

export function matchSignals (raw: string): ParsedSignals {
  const tokens = tokenize(raw)
  const consumed = new Array<boolean>(tokens.length).fill(false)

  let relationship: string | undefined
  let occasion: string | undefined
  let location: string | undefined
  const interests: string[] = []
  const surfaces: SignalSurfaces = {}

  // Only worth keeping when it says something the canonical doesn't. A city
  // matches its own name, so recording the tokenized "seattle" would just lose
  // the capitalisation the dictionary already has.
  function noteSurface (name: keyof SignalSurfaces, canonical: string, phrase: string) {
    if (normalizeKey(canonical) !== phrase) surfaces[name] = phrase
  }

  for (let i = 0; i < tokens.length;) {
    const match = longestPhraseAt(tokens, i)
    if (!match) { i += 1; continue }

    const { canonical, category, length } = match
    const phrase = tokens.slice(i, i + length).join(' ')

    if (category === 'relationship') {
      if (relationship === undefined) noteSurface('relationship', canonical, phrase)
      relationship ??= canonical
    }
    else if (category === 'occasion') {
      if (occasion === undefined) noteSurface('occasion', canonical, phrase)
      occasion ??= canonical
    }
    else if (category === 'location') {
      if (location === undefined) noteSurface('location', canonical, phrase)
      location ??= canonical
    }
    else if (!interests.includes(canonical)) { interests.push(canonical) }

    for (let j = i; j < i + length; j++) consumed[j] = true
    i += length
  }

  // Fuzzy interest pass over the leftover single tokens, so exact multi-word
  // phrases always win first.
  tokens.forEach((token, i) => {
    if (consumed[i]) return
    const canonical = fuzzyInterest(token)
    if (canonical && !interests.includes(canonical)) {
      interests.push(canonical)
      consumed[i] = true
    }
  })

  // Context-clue location: only when the city list found nothing, so curated cities
  // keep their canonical name and casing.
  if (location === undefined) {
    const context = contextLocation(tokens, consumed)
    if (context) {
      location = context.phrase
      for (let j = context.start; j < context.start + context.length; j++) consumed[j] = true
    }
  }

  const budget = matchBudget(raw)

  const signals: Partial<SignalValues> = {}
  if (relationship) signals.relationship = relationship
  if (occasion) signals.occasion = occasion
  if (location) signals.location = location
  if (interests.length) signals.interests = interests
  if (budget) signals.budget = budget.range

  const unmatched = tokens.filter((_, i) => !consumed[i])
  return { signals, surfaces, unmatched }
}

type PhraseCategory = 'relationship' | 'occasion' | 'location' | 'interest'
type PhraseMatch = { canonical: string, category: PhraseCategory, length: number }

// Longest phrase at position i wins, and among equal lengths the categories are
// tried highest-stakes first so a shared word resolves predictably.
function longestPhraseAt (tokens: string[], i: number): PhraseMatch | null {
  const maxLength = Math.min(MAX_NGRAM, tokens.length - i)
  for (let length = maxLength; length >= 1; length--) {
    const phrase = tokens.slice(i, i + length).join(' ')
    const relationship = RELATIONSHIP_LOOKUP.get(phrase)
    if (relationship) return { canonical: relationship, category: 'relationship', length }
    const occasion = OCCASION_LOOKUP.get(phrase)
    if (occasion) return { canonical: occasion, category: 'occasion', length }
    const location = CITY_LOOKUP.get(phrase)
    if (location) return { canonical: location, category: 'location', length }
    const interest = INTEREST_LOOKUP.get(phrase)
    if (interest) return { canonical: interest, category: 'interest', length }
  }
  return null
}

// Exact-miss fallback for interests only: singular/plural tolerance so "cats"
// and "cat" both land. No edit-distance guessing — matching common English
// words one edit away from a tag (e.g. "looking" → "cooking") fabricates
// interests, and the curated dictionary needs the recall less than the noise.
function fuzzyInterest (token: string): string | null {
  if (token.length < 4) return null

  const singular = token.replace(/(?:es|s)$/, '')
  if (singular !== token) {
    const canonical = INTEREST_LOOKUP.get(singular)
    if (canonical) return canonical
  }
  // Pluralizing a singular token may only hit a canonical name, never a variant,
  // so "craft" can't resolve to art via the "crafts" variant.
  for (const candidate of [`${token}s`, `${token}es`])
    if (INTEREST_NAMES.has(candidate)) return INTEREST_LOOKUP.get(candidate) ?? null

  return null
}

// Words that flag a place as their object. "to" is deliberately excluded — it
// attaches to far more than places ("wants to travel", "gift to give") — and so is
// "around", which reads as a budget qualifier ("around $50") far more often than a
// place.
const LOCATION_PREPOSITIONS = new Set(['in', 'from', 'near'])

// Where a place phrase after one of those prepositions ends. Grammar words (articles,
// conjunctions, relative pronouns, possessives, other prepositions) can't be part of
// a city name, so they close it off: "in Seattle who loves…" → "Seattle". The trailing
// nouns catch the common non-place objects — idioms ("in general", "from scratch"),
// dates ("in April", "in two weeks"), quantities — that would otherwise read as a
// one-word city.
const PLACE_PHRASE_STOP = new Set([
  'a', 'an', 'the',
  'and', 'or', 'but', 'nor', 'so', 'yet',
  'who', 'whom', 'whose', 'which', 'that', 'where', 'when',
  'for', 'with', 'to', 'of', 'on', 'at', 'by', 'as', 'into', 'about', 'over', 'under',
  'in', 'from', 'near', 'around',
  'my', 'our', 'your', 'his', 'her', 'their', 'its',
  'i', 'me', 'we', 'us', 'you', 'he', 'him', 'she', 'they', 'them', 'it',
  'general', 'particular', 'fact', 'order', 'mind', 'need', 'style', 'time',
  'advance', 'addition', 'short', 'total', 'trouble', 'common', 'love', 'town', 'scratch',
  'person', 'case', 'between', 'honor', 'memory', 'front', 'return', 'exchange',
  'january', 'february', 'march', 'april', 'may', 'june', 'july', 'august',
  'september', 'october', 'november', 'december',
  'spring', 'summer', 'autumn', 'winter', 'fall',
  'today', 'tomorrow', 'tonight', 'morning', 'afternoon', 'evening', 'night',
  'day', 'days', 'week', 'weeks', 'month', 'months', 'year', 'years',
  'hour', 'hours', 'minute', 'minutes', 'moment', 'while',
  'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'couple', 'few', 'next', 'last', 'this', 'these', 'those', 'early', 'late',
])

// A place named by context rather than by the curated city list: the word or two
// right after a location preposition ("in San Fransisco", "from Bend"). Lets a
// misspelled or simply-uncurated city reach the backend, which spell-corrects it and
// drops anything that isn't a real place — so an idiom that slips the stop set just
// yields no location once resolution comes back empty. A phrase that runs into a
// token another dictionary already matched ("interested in vintage cooking tools")
// is the preposition governing that match, not naming a place — rejected. Bare
// numbers can't start a place either ("in 3 days").
function contextLocation (
  tokens: string[],
  consumed: boolean[],
): { phrase: string, start: number, length: number } | null {
  const MAX_PLACE_TOKENS = 3
  for (let i = 0; i + 1 < tokens.length; i++) {
    if (!LOCATION_PREPOSITIONS.has(tokens[i]) || consumed[i]) continue

    const words: string[] = []
    let ranIntoMatched = false
    for (let j = i + 1; j < tokens.length && words.length < MAX_PLACE_TOKENS; j++) {
      if (consumed[j]) {
        ranIntoMatched = true
        break
      }
      if (PLACE_PHRASE_STOP.has(tokens[j]) || /^\d+$/.test(tokens[j])) break
      words.push(tokens[j])
    }
    if (ranIntoMatched || !words.length) continue
    return { phrase: words.map(titleCase).join(' '), start: i + 1, length: words.length }
  }
  return null
}

// Tokens that appear in nearly every request sentence and carry no signal on their
// own — connective tissue, request nouns, contraction shards from the tokenizer.
const FILLER_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'but', 'nor', 'so', 'yet', 'not', 'no', 'any', 'some',
  'all', 'every', 'each', 'both', 'few', 'more', 'most', 'other', 'another', 'such',
  'i', 'im', 'me', 'my', 'mine', 'we', 'our', 'us', 'you', 'your', 'yours',
  'he', 'him', 'his', 'she', 'her', 'hers', 'they', 'them', 'their', 'theirs', 'it', 'its',
  'who', 'whom', 'whose', 'which', 'that', 'what', 'this', 'these', 'those',
  'someone', 'somebody', 'anyone', 'anybody', 'something', 'anything', 'one',
  'for', 'with', 'to', 'of', 'on', 'at', 'by', 'as', 'in', 'from', 'near', 'around',
  'into', 'about', 'over', 'under', 'up', 'out', 'off',
  'is', 'are', 'was', 'were', 'am', 'be', 'been', 'being',
  'do', 'does', 'did', 'will', 'would', 'can', 'could', 'should', 'shall', 'might', 'must',
  'have', 'has', 'had', 'get', 'gets', 'getting', 'got', 'give', 'gives', 'giving',
  'find', 'finds', 'finding', 'buy', 'buys', 'buying', 'shop', 'shopping',
  'look', 'looks', 'looking', 'search', 'searching',
  'want', 'wants', 'wanting', 'need', 'needs', 'needing',
  'like', 'likes', 'love', 'loves', 'enjoy', 'enjoys', 'hate', 'hates', 'prefer', 'prefers',
  'know', 'knows', 'think', 'thinks', 'go', 'going', 'goes', 'make', 'makes',
  'gift', 'gifts', 'present', 'presents', 'idea', 'ideas', 'thing', 'things', 'stuff',
  'item', 'items', 'option', 'options', 'suggestion', 'suggestions',
  'really', 'very', 'super', 'pretty', 'kind', 'kinda', 'sort', 'just', 'only', 'maybe',
  'also', 'too', 'well', 'now', 'here', 'there', 'nice', 'good', 'great', 'cool', 'fun',
  'new', 'special', 'perfect', 'big', 'small', 'little',
  'please', 'thanks', 'thank', 'hi', 'hey', 'hello', 'ok', 'okay', 'yes', 'yeah', 'help',
  // The left halves of split negations: the tokenizer turns apostrophes into
  // spaces, so "don't" arrives as "don" + "t".
  'don', 'didn', 'doesn', 'isn', 'wasn', 'weren', 'aren', 'couldn', 'wouldn',
  'shouldn', 'hasn', 'haven', 'hadn', 'won', 'ain', 'needn', 'mustn', 'shan',
])

export function hasUnplacedContent (unmatched: string[]): boolean {
  // Tokens of 1-2 characters are never a recoverable signal — mostly the right
  // halves of split contractions ("i'm" → "i" + "m", "she'll" → "she" + "ll").
  return unmatched.some(token => token.length > 2 && !FILLER_WORDS.has(token))
}

function titleCase (token: string): string {
  return token.charAt(0).toUpperCase() + token.slice(1)
}

// Hyphens and apostrophes become spaces to match the dictionary keys, so
// "mother-in-law" and "my dad's" split into the same tokens the lookups use.
function tokenize (raw: string): string[] {
  return raw
    .toLowerCase()
    .replace(/[-'’]/g, ' ')
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
}
